/**
 * Canvas 2D pour dessiner le plan du logement — F-SAIS-002, F-SAIS-005.
 *
 * Implémentation V2 : DOM HTML + react-rnd au lieu de Konva.
 *
 * Pourquoi le changement ? L'implémentation Konva (Stage + Transformer +
 * hit-detection canvas) s'est révélée trop fragile à debugger : les events
 * mousedown atteignaient le Stage parfois sans être propagés aux Rects, les
 * ancres du Transformer s'occultaient mutuellement avec les Rects voisins,
 * etc. react-rnd s'appuie sur de simples `<div>` HTML positionnés en
 * absolute — les events sont gérés nativement par le browser, sans
 * couches de hit-detection à reconstruire.
 *
 * UX :
 *   1. Bouton "+ Ajouter une pièce" → rectangle 4 × 3 m au centre,
 *      sélectionné d'office.
 *   2. Drag du corps → déplacement (bounded au canvas).
 *      Drag d'un coin → redimensionnement (4 coins).
 *      Snap à la grille (0,5 m) à la release.
 *   3. Inspector latéral : édition numérique + suppression. Touche Suppr.
 *
 * Le format `PlanGeometry` est inchangé pour compatibilité avec le parent
 * (LogementDPE et `geometryToWallSurface`).
 */

import { memo, useEffect, useState } from "react";
import { Rnd } from "react-rnd";

const STAGE_WIDTH = 700;
const STAGE_HEIGHT = 450;
const GRID_SIZE_PX = 20;
/** 1 case de grille = 0,5 m. Canvas 700 × 450 ≈ 17,5 × 11,25 m. */
const DEFAULT_METERS_PER_CELL = 0.5;
const MIN_ROOM_PX = GRID_SIZE_PX; // 0,5 m minimum

export type RoomShape = {
  id: string;
  x_px: number;
  y_px: number;
  width_px: number;
  height_px: number;
};

export type PlanGeometry = {
  rooms: RoomShape[];
  meters_per_cell: number;
};

// =============================================================================
// Conversions & calculs (exportés — gardent la signature attendue par le parent)
// =============================================================================

export function geometryToWallSurface(
  geom: PlanGeometry,
  ceilingHeightM: number,
): {
  total_perimeter_m: number;
  total_floor_area_m2: number;
  total_wall_surface_m2: number;
} {
  const px2m = geom.meters_per_cell / GRID_SIZE_PX;
  let perimeter = 0;
  let floor = 0;
  for (const r of geom.rooms) {
    const w_m = Math.abs(r.width_px) * px2m;
    const h_m = Math.abs(r.height_px) * px2m;
    perimeter += 2 * (w_m + h_m);
    floor += w_m * h_m;
  }
  return {
    total_perimeter_m: round1(perimeter),
    total_floor_area_m2: round1(floor),
    total_wall_surface_m2: round1(perimeter * ceilingHeightM),
  };
}

export function emptyPlan(): PlanGeometry {
  return { rooms: [], meters_per_cell: DEFAULT_METERS_PER_CELL };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function snapToGrid(v: number): number {
  return Math.round(v / GRID_SIZE_PX) * GRID_SIZE_PX;
}

// =============================================================================
// Composant principal
// =============================================================================

export function WizardPlanCanvas({
  value,
  onChange,
  ceilingHeightM = 2.5,
}: {
  value: PlanGeometry;
  onChange: (next: PlanGeometry) => void;
  ceilingHeightM?: number;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const pxPerMeter = GRID_SIZE_PX / value.meters_per_cell;

  // Suppression au clavier sur la pièce sélectionnée
  useEffect(() => {
    if (!selectedId) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeRoom(selectedId);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const addRoom = (widthM: number, heightM: number) => {
    const w = snapToGrid(widthM * pxPerMeter);
    const h = snapToGrid(heightM * pxPerMeter);
    let x = snapToGrid(STAGE_WIDTH / 2 - w / 2);
    let y = snapToGrid(STAGE_HEIGHT / 2 - h / 2);
    const placementMargin = GRID_SIZE_PX * 2;
    const overlaps = (xx: number, yy: number) =>
      value.rooms.some(
        (r) =>
          xx < r.x_px + r.width_px + placementMargin &&
          xx + w + placementMargin > r.x_px &&
          yy < r.y_px + r.height_px + placementMargin &&
          yy + h + placementMargin > r.y_px,
      );
    let safety = 0;
    while (overlaps(x, y) && safety++ < 100) {
      x += GRID_SIZE_PX * 2;
      if (x + w > STAGE_WIDTH - GRID_SIZE_PX) {
        x = snapToGrid(GRID_SIZE_PX);
        y += GRID_SIZE_PX * 2;
      }
      if (y + h > STAGE_HEIGHT - GRID_SIZE_PX) break;
    }
    // crypto.randomUUID() — unique sans collision même sur création rapide
    // (vs Date.now()+Math.random() qui collisionne sur millis identiques).
    const newRoom: RoomShape = {
      id: `r-${crypto.randomUUID()}`,
      x_px: x,
      y_px: y,
      width_px: w,
      height_px: h,
    };
    onChange({ ...value, rooms: [...value.rooms, newRoom] });
    setSelectedId(newRoom.id);
  };

  const updateRoom = (id: string, partial: Partial<RoomShape>) => {
    onChange({
      ...value,
      rooms: value.rooms.map((r) => (r.id === id ? { ...r, ...partial } : r)),
    });
  };

  const removeRoom = (id: string) => {
    onChange({ ...value, rooms: value.rooms.filter((r) => r.id !== id) });
    setSelectedId(null);
  };

  const clearAll = () => {
    onChange({ ...value, rooms: [] });
    setSelectedId(null);
  };

  const selectedRoom = value.rooms.find((r) => r.id === selectedId) ?? null;
  const derived = geometryToWallSurface(value, ceilingHeightM);

  return (
    <div>
      <Toolbar onAdd={addRoom} onClear={clearAll} hasRooms={value.rooms.length > 0} />

      <p className="mb-2 text-xs text-gray-600">
        Échelle : 1 case = 0,5 m (lignes plus foncées tous les mètres).
        Cliquez sur une pièce pour la sélectionner, glissez-la pour la déplacer,
        attrapez les poignées des 4 coins pour la redimensionner.
      </p>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <PlanArea
          rooms={value.rooms}
          selectedId={selectedId}
          pxPerMeter={pxPerMeter}
          onSelect={setSelectedId}
          onChange={updateRoom}
        />

        <RoomInspector
          room={selectedRoom}
          pxPerMeter={pxPerMeter}
          onChange={(partial) => selectedRoom && updateRoom(selectedRoom.id, partial)}
          onRemove={() => selectedRoom && removeRoom(selectedRoom.id)}
        />
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <Stat label="Pièces dessinées" value={`${value.rooms.length}`} />
        <Stat
          label="Surface au sol (calc.)"
          value={`${derived.total_floor_area_m2} m²`}
        />
        <Stat
          label="Surface murs estimée"
          value={`${derived.total_wall_surface_m2} m² (H ${ceilingHeightM} m)`}
        />
      </dl>
    </div>
  );
}

// =============================================================================
// Zone de plan : grille en background CSS + rooms en absolute via react-rnd
// =============================================================================

function PlanArea({
  rooms,
  selectedId,
  pxPerMeter,
  onSelect,
  onChange,
}: {
  rooms: RoomShape[];
  selectedId: string | null;
  pxPerMeter: number;
  onSelect: (id: string | null) => void;
  onChange: (id: string, partial: Partial<RoomShape>) => void;
}) {
  // Grille en background CSS : lignes mineures tous les 0,5 m + lignes
  // majeures tous les mètres. Bien plus simple que de dessiner sur canvas.
  const gridBg = {
    backgroundColor: "#ffffff",
    backgroundImage: [
      // Lignes mineures (chaque 20px = 0,5 m)
      `linear-gradient(to right, #f1f3f5 1px, transparent 1px)`,
      `linear-gradient(to bottom, #f1f3f5 1px, transparent 1px)`,
      // Lignes majeures (chaque 40px = 1 m)
      `linear-gradient(to right, #d1d5db 1px, transparent 1px)`,
      `linear-gradient(to bottom, #d1d5db 1px, transparent 1px)`,
    ].join(", "),
    backgroundSize: `${GRID_SIZE_PX}px ${GRID_SIZE_PX}px, ${GRID_SIZE_PX}px ${GRID_SIZE_PX}px, ${GRID_SIZE_PX * 2}px ${GRID_SIZE_PX * 2}px, ${GRID_SIZE_PX * 2}px ${GRID_SIZE_PX * 2}px`,
  };

  return (
    <div
      className="rounded border border-gray-300"
      style={{
        ...gridBg,
        width: STAGE_WIDTH,
        height: STAGE_HEIGHT,
        position: "relative",
        flexShrink: 0,
        flexGrow: 0,
        overflow: "hidden",
      }}
      onMouseDown={(e) => {
        // Clic sur la zone vide (pas sur une pièce) → désélection
        if (e.target === e.currentTarget) {
          onSelect(null);
        }
      }}
    >
      {rooms.map((room) => (
        <RoomDraggable
          key={room.id}
          room={room}
          isSelected={room.id === selectedId}
          pxPerMeter={pxPerMeter}
          onSelect={() => onSelect(room.id)}
          onChange={(partial) => onChange(room.id, partial)}
        />
      ))}
    </div>
  );
}

const RoomDraggable = memo(function RoomDraggable({
  room,
  isSelected,
  pxPerMeter,
  onSelect,
  onChange,
}: {
  room: RoomShape;
  isSelected: boolean;
  pxPerMeter: number;
  onSelect: () => void;
  onChange: (partial: Partial<RoomShape>) => void;
}) {
  const px2m = 1 / pxPerMeter;
  // État local pour les dimensions live pendant un resize en cours. react-rnd
  // gère sa taille interne (visible), mais la prop `size` ne se met à jour
  // que via onResizeStop. On capture la taille à chaque onResize pour
  // afficher les cotes en temps réel dans le label.
  const [liveSize, setLiveSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const displayWidthPx = liveSize?.width ?? room.width_px;
  const displayHeightPx = liveSize?.height ?? room.height_px;
  const w_m = round1(displayWidthPx * px2m);
  const h_m = round1(displayHeightPx * px2m);

  return (
    <Rnd
      size={{ width: room.width_px, height: room.height_px }}
      position={{ x: room.x_px, y: room.y_px }}
      bounds="parent"
      // Snap-to-grid intégré : multiples de 20px pour position et taille
      dragGrid={[GRID_SIZE_PX, GRID_SIZE_PX]}
      resizeGrid={[GRID_SIZE_PX, GRID_SIZE_PX]}
      minWidth={MIN_ROOM_PX}
      minHeight={MIN_ROOM_PX}
      enableResizing={{
        top: false,
        right: false,
        bottom: false,
        left: false,
        topLeft: isSelected,
        topRight: isSelected,
        bottomLeft: isSelected,
        bottomRight: isSelected,
      }}
      onDragStart={onSelect}
      onResizeStart={onSelect}
      onDragStop={(_e, d) => {
        onChange({ x_px: d.x, y_px: d.y });
      }}
      onResize={(_e, _dir, ref) => {
        setLiveSize({ width: ref.offsetWidth, height: ref.offsetHeight });
      }}
      onResizeStop={(_e, _dir, ref, _delta, pos) => {
        setLiveSize(null);
        onChange({
          x_px: pos.x,
          y_px: pos.y,
          width_px: ref.offsetWidth,
          height_px: ref.offsetHeight,
        });
      }}
      style={{
        background: isSelected ? "rgba(30, 64, 175, 0.25)" : "rgba(30, 64, 175, 0.12)",
        border: `${isSelected ? 3 : 2}px solid #1e40af`,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "move",
        userSelect: "none",
      }}
      // Sélection au mousedown : assure que le clic sur le corps sélectionne
      // même si l'utilisateur ne déplace pas.
      onMouseDown={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      resizeHandleStyles={{
        topLeft: cornerHandleStyle("nwse-resize"),
        topRight: cornerHandleStyle("nesw-resize"),
        bottomLeft: cornerHandleStyle("nesw-resize"),
        bottomRight: cornerHandleStyle("nwse-resize"),
      }}
    >
      <span
        style={{
          color: "#1e40af",
          fontWeight: 600,
          fontSize: 13,
          pointerEvents: "none",
        }}
      >
        {w_m} × {h_m} m
      </span>
    </Rnd>
  );
});

function cornerHandleStyle(cursor: string): React.CSSProperties {
  return {
    width: 14,
    height: 14,
    background: "#ffffff",
    border: "2px solid #1e40af",
    borderRadius: 2,
    cursor,
  };
}

// =============================================================================
// Sous-composants UI
// =============================================================================

function Toolbar({
  onAdd,
  onClear,
  hasRooms,
}: {
  onAdd: (widthM: number, heightM: number) => void;
  onClear: () => void;
  hasRooms: boolean;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onAdd(4, 3)}
        className="rounded bg-blue-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800"
      >
        + Ajouter une pièce
      </button>
      <div className="flex items-center gap-1 text-xs text-gray-500">
        <span>ou pré-réglages :</span>
        <PresetButton onClick={() => onAdd(5, 4)} label="Séjour 5×4" />
        <PresetButton onClick={() => onAdd(3, 3)} label="Chambre 3×3" />
        <PresetButton onClick={() => onAdd(2, 2)} label="SdB 2×2" />
        <PresetButton onClick={() => onAdd(8, 6)} label="Maison 8×6" />
      </div>
      {hasRooms && (
        <button
          type="button"
          onClick={onClear}
          className="ml-auto rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
        >
          Tout effacer
        </button>
      )}
    </div>
  );
}

function PresetButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-100"
    >
      {label}
    </button>
  );
}

function RoomInspector({
  room,
  pxPerMeter,
  onChange,
  onRemove,
}: {
  room: RoomShape | null;
  pxPerMeter: number;
  onChange: (partial: Partial<RoomShape>) => void;
  onRemove: () => void;
}) {
  if (!room) {
    return (
      <div className="w-full rounded border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500 lg:w-56">
        <p className="font-medium text-gray-700">Aucune pièce sélectionnée</p>
        <p className="mt-2 text-xs">
          Cliquez sur une pièce du plan, ou ajoutez-en une avec le bouton ci-dessus.
        </p>
      </div>
    );
  }

  const px2m = 1 / pxPerMeter;
  const w_m = round1(room.width_px * px2m);
  const h_m = round1(room.height_px * px2m);

  const setW = (newW: number) => {
    if (!Number.isFinite(newW) || newW <= 0) return;
    onChange({ width_px: snapToGrid(newW * pxPerMeter) });
  };
  const setH = (newH: number) => {
    if (!Number.isFinite(newH) || newH <= 0) return;
    onChange({ height_px: snapToGrid(newH * pxPerMeter) });
  };

  return (
    <div className="w-full rounded border border-blue-200 bg-blue-50 p-4 text-sm lg:w-56">
      <p className="mb-3 font-medium text-blue-900">Pièce sélectionnée</p>
      <div className="space-y-2">
        <NumberInput label="Largeur (m)" value={w_m} onChange={setW} />
        <NumberInput label="Hauteur (m)" value={h_m} onChange={setH} />
        <p className="pt-1 text-xs text-blue-900">
          Surface : <strong>{round1(w_m * h_m)} m²</strong>
        </p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="mt-4 w-full rounded border border-red-300 px-3 py-1 text-xs text-red-700 hover:bg-red-100"
      >
        Supprimer cette pièce
      </button>
      <p className="mt-2 text-[10px] text-gray-500">
        Astuce : touche Suppr pour supprimer rapidement.
      </p>
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-700">{label}</span>
      <input
        type="number"
        step="0.5"
        min="1"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
      />
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded bg-gray-50 p-2">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 font-medium">{value}</dd>
    </div>
  );
}
